import React, { useState, type MouseEvent } from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export interface MenuItemConfig {
  label?: string; // Label for the menu item (optional if it's a divider)
  onClick?: () => void; // Simplified onClick handler without params
  isDivider?: boolean; // Flag to indicate if this is a divider
  disabled?: boolean; // Flag to disable the menu item
  icon?: React.ReactNode; // Optional icon to display before the label
  style?: React.CSSProperties; // Optional inline styles for the menu item
  className?: string; // Optional CSS class name for the menu item
  hidden?: boolean; // New prop to hide the menu item
}

export interface KebabMenuProps {
  getMenuItems: () => MenuItemConfig[];
}

export const KebabMenu = ({ getMenuItems }: KebabMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  // Get menu items only when needed
  const menuItems = getMenuItems();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (onClick?: () => void) => {
    if (onClick) {
      onClick();
    }
    handleClose();
  };

  return (
    <div>
      <IconButton
        aria-label="more"
        aria-controls={open ? 'kebab-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="kebab-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'kebab-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
          {menuItems
          .filter(item => !item.hidden)
          .map((item, index) => {
            if (item.isDivider) {
              return <Divider key={`divider-${index}`} />;
            }
            return (
              <MenuItem
                key={item.label || index}
                onClick={() => handleMenuItemClick(item.onClick)}
                disabled={item.disabled}
                style={item.style}
                className={item.className}
              >
                {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
                {item.label}
              </MenuItem>
            );
          })}
      </Menu>
    </div>
  );
};